import { useMemo, useState } from 'react';
import { X } from 'lucide-react';
import { useWorkflow } from '../hooks/useWorkflow';
import { WorkflowStep } from './WorkflowStep';
import { Progress } from '@/components/ui/progress';
import { accountScoped } from '@/lib/activeAccount';

interface WorkflowWidgetProps {
  workflowId: string;
}

const getDismissedSteps = (workflowId: string): Set<string> => {
  const key = `workflow-dismissed-steps-${workflowId}`;
  const stored = localStorage.getItem(accountScoped(key));
  return stored ? new Set(JSON.parse(stored) as string[]) : new Set();
};

const setDismissedSteps = (workflowId: string, dismissed: Set<string>) => {
  const key = `workflow-dismissed-steps-${workflowId}`;
  localStorage.setItem(accountScoped(key), JSON.stringify([...dismissed]));
};

export function WorkflowWidget({ workflowId }: WorkflowWidgetProps) {
  const { definition, steps, dismissed, dismiss, isLoading, error } =
    useWorkflow(workflowId);

  const [dismissedSteps, setDismissedStepsState] = useState<Set<string>>(() =>
    getDismissedSteps(workflowId),
  );

  const adjustedProgress = useMemo(() => {
    if (!steps) return { completed: 0, total: 0 };
    let completed = 0;
    let total = 0;
    definition.steps.forEach((step) => {
      if (dismissedSteps.has(step.id)) return;
      total++;
      if (steps[step.id]) completed++;
    });
    return { completed, total };
  }, [steps, definition.steps, dismissedSteps]);

  const handleDismissStep = (stepId: string) => {
    const newDismissed = new Set(dismissedSteps);
    newDismissed.add(stepId);
    setDismissedStepsState(newDismissed);
    setDismissedSteps(workflowId, newDismissed);
  };

  if (
    dismissed ||
    (adjustedProgress.completed === adjustedProgress.total && adjustedProgress.total > 0)
  ) {
    return null;
  }

  if (isLoading || error || !steps) return null;

  const progressPercentage =
    adjustedProgress.total > 0
      ? Math.round((adjustedProgress.completed / adjustedProgress.total) * 100)
      : 0;

  return (
    <section className="rounded-lg border border-border bg-card p-4">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <h3 className="text-base font-semibold mb-1">{definition.title}</h3>
          {definition.description ? (
            <p className="text-sm text-muted-foreground">{definition.description}</p>
          ) : null}
        </div>
        <button
          type="button"
          onClick={() => { dismiss(); }}
          className="shrink-0 inline-flex h-6 w-6 items-center justify-center rounded text-muted-foreground hover:bg-accent"
          aria-label="Dismiss workflow"
          title="Dismiss entire workflow"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>

      <div className="mb-4">
        <div className="flex items-center justify-between mb-2 text-xs">
          <span className="text-muted-foreground">
            {adjustedProgress.completed} of {adjustedProgress.total} completed
          </span>
          <span className="font-semibold">{progressPercentage}%</span>
        </div>
        <Progress value={progressPercentage} className="h-2" />
      </div>

      <div className="space-y-2">
        {definition.steps.map((step) => (
          <WorkflowStep
            key={step.id}
            step={step}
            completed={steps[step.id] ?? false}
            dismissed={dismissedSteps.has(step.id)}
            onDismiss={() => { handleDismissStep(step.id); }}
          />
        ))}
      </div>
    </section>
  );
}
